import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './TokenAssetDetails.scss'

import iconBack from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-uvbh67v.svg'
import iconChevron from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-aosihid.svg'
import iconChecklist from '../../../../../Assets/Icon/AdminTradersCard/mna2n49e-02tuta8.svg'
import iconApprove from '../../../../../Assets/Icon/AdminTradersCard/mna2n3vx-tmi2guh.svg'
import iconPrev from '../../../../../Assets/Images/AdminTradersCard/mna2n321-kd3kkq4.png'
import iconNext from '../../../../../Assets/Icon/AdminTradersCard/mna2n322-lk9t334.svg'

import investorTokenAssetsService, {
  type InvestorTokenAsset,
  type InvestorTokenAssetPayout,
} from 'Services/investorTokenAssetsService'
import { TimeConverter } from 'Helpers/TimeConverter'
import { ModalWrapper } from 'Components'

type TokenAssetStatusRoute = 'ALL' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED' | 'LIQUIDATED'

const formatNairaMajor = (value: number | undefined | null) => {
  if (value === undefined || value === null) return '-'
  return `₦${value.toLocaleString()}`
}

const TokenAssetDetails: React.FC = () => {
  const { status: statusParam, tokenAssetId } = useParams()
  const s = (statusParam ?? 'ALL') as TokenAssetStatusRoute
  const isValidStatus =
    s === 'ALL' || s === 'ACTIVE' || s === 'COMPLETED' || s === 'CANCELLED' || s === 'LIQUIDATED'

  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [asset, setAsset] = useState<InvestorTokenAsset | null>(null)

  const [payoutLoading, setPayoutLoading] = useState(false)
  const [payoutError, setPayoutError] = useState<string | null>(null)
  const [payouts, setPayouts] = useState<InvestorTokenAssetPayout[]>([])
  const [payoutPreview, setPayoutPreview] = useState<InvestorTokenAssetPayout | null>(null)
  const [payoutPageNumber, setPayoutPageNumber] = useState(1)
  const [payoutPageSize] = useState(20)
  const [payoutPagination, setPayoutPagination] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({ pageNumber: 1, pageSize: 20, total: 0 })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  const fetchAsset = useCallback(async () => {
    if (!tokenAssetId) return
    setLoading(true)
    setErrorMessage(null)
    try {
      const response = await investorTokenAssetsService.getSingle(tokenAssetId)
      setAsset((response?.data?.data ?? null) as InvestorTokenAsset | null)
    } catch (e) {
      setAsset(null)
      setErrorMessage(getErrorMessage(e) ?? 'Failed to load token asset')
    } finally {
      setLoading(false)
    }
  }, [tokenAssetId])

  const fetchPayouts = useCallback(async () => {
    if (!tokenAssetId) return
    setPayoutLoading(true)
    setPayoutError(null)
    try {
      const response = await investorTokenAssetsService.listPayouts({
        pageNumber: payoutPageNumber,
        pageSize: payoutPageSize,
        sortOrder: 'ASC',
        investorTokenAssetId: tokenAssetId,
      })
      const data = response?.data?.data
      setPayouts((data?.dataset ?? []) as InvestorTokenAssetPayout[])
      setPayoutPagination({
        pageNumber: Number(data?.pageNumber ?? payoutPageNumber),
        pageSize: Number(data?.pageSize ?? payoutPageSize),
        total: Number(data?.total ?? 0),
      })
    } catch (e) {
      setPayouts([])
      setPayoutPagination({ pageNumber: payoutPageNumber, pageSize: payoutPageSize, total: 0 })
      setPayoutError(getErrorMessage(e) ?? 'Failed to load payouts')
    } finally {
      setPayoutLoading(false)
    }
  }, [payoutPageNumber, payoutPageSize, tokenAssetId])

  useEffect(() => {
    if (!isValidStatus || !tokenAssetId) return
    fetchAsset()
  }, [fetchAsset, isValidStatus, tokenAssetId])

  useEffect(() => {
    if (!isValidStatus || !tokenAssetId) return
    fetchPayouts()
  }, [fetchPayouts, isValidStatus, tokenAssetId])

  if (!isValidStatus || !tokenAssetId) return <Navigate to='/blockchain/token-assets/ALL' replace />

  const shares = useMemo(() => {
    const s = asset?.shares ?? []
    return Array.isArray(s) ? s : []
  }, [asset])

  const onTriggerPayout = async () => {
    if (!tokenAssetId) return
    if (!window.confirm('Trigger payout for this token asset?')) return
    setLoading(true)
    setErrorMessage(null)
    try {
      await investorTokenAssetsService.triggerPayout(tokenAssetId)
      await fetchAsset()
      await fetchPayouts()
    } catch (e) {
      setErrorMessage(getErrorMessage(e) ?? 'Failed to trigger payout')
    } finally {
      setLoading(false)
    }
  }

  const payoutTotalPages = Math.max(
    1,
    Math.ceil(payoutPagination.total / payoutPagination.pageSize),
  )

  return (
    <div className='admin-token-asset-details'>
      <div className='admin-token-asset-details__breadcrumb'>
        <Link to={`/blockchain/token-assets/${s}`} className='admin-token-asset-details__crumb'>
          <img src={iconBack} alt='' className='admin-token-asset-details__crumb-icon' />
          <span>Token Assets</span>
        </Link>
        <img src={iconChevron} alt='' className='admin-token-asset-details__crumb-sep' />
        <span className='admin-token-asset-details__crumb-current'>Asset Details</span>
      </div>

      <div className='admin-token-asset-details__top'>
        <div className='admin-token-asset-details__title'>
          <span className='admin-token-asset-details__title-label'>Token Asset:</span>
          <span className='admin-token-asset-details__title-name'>{asset?.id ?? tokenAssetId}</span>
        </div>
        <div className='admin-token-asset-details__actions'>
          <button
            type='button'
            className='admin-token-asset-details__trigger'
            onClick={onTriggerPayout}
            disabled={loading}
          >
            <img src={iconApprove} alt='' className='admin-token-asset-details__trigger-icon' />
            <span>Trigger Payout</span>
          </button>
        </div>
      </div>

      {!loading && errorMessage ? <div className='mt-3'>{errorMessage}</div> : null}

      <div className='row g-3 g-xl-4 mt-2'>
        <div className='col-12 col-xl-6'>
          <div className='admin-ta-card'>
            <div className='admin-ta-card__title'>Asset Information</div>
            <div className='admin-ta-info'>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Account ID</div>
                <div className='admin-ta-info__value'>{asset?.createdByAccountId ?? '-'}</div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Status</div>
                <div className='admin-ta-info__value'>{asset?.status ?? '-'}</div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Purchase Date</div>
                <div className='admin-ta-info__value'>{TimeConverter(asset?.purchaseDate)}</div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Tokens Purchased</div>
                <div className='admin-ta-info__value'>{asset?.tokensPurchased ?? '-'}</div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Principal</div>
                <div className='admin-ta-info__value'>
                  {formatNairaMajor(asset?.principalNairaMajor)}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Monthly ROI</div>
                <div className='admin-ta-info__value'>
                  {asset?.percentageReturnMonthly != null
                    ? `${asset.percentageReturnMonthly}%`
                    : '-'}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Term</div>
                <div className='admin-ta-info__value'>
                  {asset?.termMonths != null ? `${asset.termMonths} months` : '-'}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Monthly Payment</div>
                <div className='admin-ta-info__value'>
                  {formatNairaMajor(asset?.monthlyPaymentNairaMajor)}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Months Paid</div>
                <div className='admin-ta-info__value'>{asset?.monthsPaid ?? '-'}</div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Total Paid Out</div>
                <div className='admin-ta-info__value'>
                  {formatNairaMajor(asset?.totalPaidOutNairaMajor)}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Remaining Payout</div>
                <div className='admin-ta-info__value'>
                  {formatNairaMajor(asset?.totalRemainingPayoutNairaMajor)}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Next Payout Due</div>
                <div className='admin-ta-info__value'>{TimeConverter(asset?.nextPayoutDueAt)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className='col-12 col-xl-6'>
          <div className='admin-ta-card'>
            <div className='admin-ta-card__title'>Blockchain Info</div>
            <div className='admin-ta-info'>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Tx Hash</div>
                <div className='admin-ta-info__value'>
                  {asset?.blockchainInfo?.blockchainTxHash ?? '-'}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Block Number</div>
                <div className='admin-ta-info__value'>
                  {asset?.blockchainInfo?.blockchainBlockNumber ?? '-'}
                </div>
              </div>
              <div className='admin-ta-info__row'>
                <div className='admin-ta-info__label'>Registered At</div>
                <div className='admin-ta-info__value'>
                  {TimeConverter(asset?.blockchainInfo?.blockchainRegisteredAt)}
                </div>
              </div>
            </div>
          </div>

          <div className='admin-ta-card mt-3 mt-xl-4'>
            <div className='admin-ta-card__title'>Shares</div>
            <div className='admin-ta-docs'>
              {shares.length === 0 ? (
                <div className='admin-ta-doc'>
                  <div className='admin-ta-doc__left'>
                    <img src={iconChecklist} alt='' className='admin-ta-doc__icon' />
                    <div className='admin-ta-doc__meta'>
                      <div className='admin-ta-doc__name'>No shares</div>
                    </div>
                  </div>
                </div>
              ) : (
                shares.map((sh, idx) => (
                  <React.Fragment key={`${sh.accountId ?? idx}`}>
                    <div className='admin-ta-doc'>
                      <div className='admin-ta-doc__left'>
                        <img src={iconChecklist} alt='' className='admin-ta-doc__icon' />
                        <div className='admin-ta-doc__meta'>
                          <div className='admin-ta-doc__name'>{sh.accountId ?? '-'}</div>
                          <div className='admin-ta-doc__size'>{sh.tokenCountOwned ?? 0} tokens</div>
                        </div>
                      </div>
                    </div>
                    {idx !== shares.length - 1 ? <div className='admin-ta-doc__divider' /> : null}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='admin-ta-card mt-3 mt-xl-4'>
        <div className='admin-ta-card__title'>Payouts</div>

        <div className='admin-token-asset-details-table'>
          <div className='admin-token-asset-details-table__head admin-token-asset-details-table__grid'>
            <div>Payout ID</div>
            <div>Period</div>
            <div>Amount</div>
            <div>Paid At</div>
            <div className='text-end'>Action</div>
          </div>

          <div className='admin-token-asset-details-table__body'>
            {payoutLoading && (
              <div className='admin-token-asset-details-table__row admin-token-asset-details-table__grid'>
                <div className='admin-token-asset-details-table__cell'>Loading...</div>
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
              </div>
            )}

            {!payoutLoading && payoutError && (
              <div className='admin-token-asset-details-table__row admin-token-asset-details-table__grid'>
                <div className='admin-token-asset-details-table__cell'>{payoutError}</div>
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
              </div>
            )}

            {!payoutLoading && !payoutError && payouts.length === 0 && (
              <div className='admin-token-asset-details-table__row admin-token-asset-details-table__grid'>
                <div className='admin-token-asset-details-table__cell'>No payouts found</div>
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
                <div className='admin-token-asset-details-table__cell' />
              </div>
            )}

            {!payoutLoading &&
              !payoutError &&
              payouts.map((p) => (
                <div
                  key={p.id}
                  className='admin-token-asset-details-table__row admin-token-asset-details-table__grid'
                >
                  <div className='admin-token-asset-details-table__cell'>{p.id}</div>
                  <div className='admin-token-asset-details-table__cell'>
                    {p.periodNumber ?? '-'}
                  </div>
                  <div className='admin-token-asset-details-table__cell'>
                    {formatNairaMajor(p.payoutTotalNairaMajor)}
                  </div>
                  <div className='admin-token-asset-details-table__cell'>
                    {TimeConverter(p.paidAt)}
                  </div>
                  <div className='admin-token-asset-details-table__cell text-end'>
                    <button
                      type='button'
                      className='admin-token-asset-details-table__action'
                      onClick={() => setPayoutPreview(p)}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
          </div>

          <div className='admin-token-asset-details-pagination'>
            <button
              type='button'
              className='admin-token-asset-details-pagination__button'
              onClick={() => setPayoutPageNumber((p) => Math.max(1, p - 1))}
              disabled={payoutPageNumber <= 1 || payoutLoading}
            >
              <img src={iconPrev} alt='' className='admin-token-asset-details-pagination__icon' />
              <span>Previous</span>
            </button>
            <div className='admin-token-asset-details-pagination__numbers'>
              Page {payoutPageNumber} of {payoutTotalPages}
            </div>
            <button
              type='button'
              className='admin-token-asset-details-pagination__button'
              onClick={() => setPayoutPageNumber((p) => Math.min(payoutTotalPages, p + 1))}
              disabled={payoutPageNumber >= payoutTotalPages || payoutLoading}
            >
              <span>Next</span>
              <img src={iconNext} alt='' className='admin-token-asset-details-pagination__icon' />
            </button>
          </div>
        </div>
      </div>

      <ModalWrapper
        showModal={Boolean(payoutPreview)}
        closeModal={() => setPayoutPreview(null)}
        title='Payout Details'
        size='lg'
      >
        {payoutPreview ? (
          <div className='admin-token-asset-details-payout-preview'>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Payout ID</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {payoutPreview.id}
              </div>
            </div>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Asset ID</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {payoutPreview.investorTokenAssetId ?? tokenAssetId}
              </div>
            </div>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Period</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {payoutPreview.periodNumber ?? '-'}
              </div>
            </div>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Amount</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {formatNairaMajor(payoutPreview.payoutTotalNairaMajor)}
              </div>
            </div>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Paid At</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {TimeConverter(payoutPreview.paidAt)}
              </div>
            </div>
            <div className='admin-token-asset-details-payout-preview__row'>
              <div className='admin-token-asset-details-payout-preview__label'>Breakdown</div>
              <div className='admin-token-asset-details-payout-preview__value'>
                {payoutPreview.breakdown ?? '-'}
              </div>
            </div>
          </div>
        ) : null}
      </ModalWrapper>
    </div>
  )
}

export default TokenAssetDetails
